/**
 * Razorpay Standard Checkout — shared helper for all PG flows.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let scriptPromise = null;

function loadRazorpayScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Razorpay requires a browser'));
  }
  if (window.Razorpay) {
    return Promise.resolve();
  }
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Razorpay checkout.js'));
    document.body.appendChild(s);
  });
  return scriptPromise;
}

/**
 * @param {Object} opts
 * @param {Object} opts.createOrderPayload - body for POST /api/payment/razorpay/create-order
 * @param {Object} [opts.prefill] - { name, email, contact }
 * @param {string} [opts.description]
 * @param {Function} [opts.onVerified] - (verifyResponse) => void
 * @param {Function} [opts.onDismiss] - () => void
 */
export async function openRazorpayCheckout({
  createOrderPayload,
  prefill = {},
  description = 'ACF Tourism Payment',
  onVerified,
  onDismiss,
}) {
  await loadRazorpayScript();

  const orderResp = await axios.post(
    `${BACKEND_URL}/api/payment/razorpay/create-order`,
    createOrderPayload,
    { withCredentials: true },
  );
  const {
    key_id,
    razorpay_order_id,
    amount,
    currency,
    receipt,
  } = orderResp.data;

  return new Promise((resolve, reject) => {
    const options = {
      key: key_id,
      amount,
      currency: currency || 'INR',
      name: 'ACF Tourism',
      description,
      order_id: razorpay_order_id,
      prefill: {
        name: prefill.name || '',
        email: prefill.email || '',
        contact: prefill.contact || '',
      },
      handler: async (response) => {
        try {
          const verifyResp = await axios.post(
            `${BACKEND_URL}/api/payment/razorpay/verify`,
            {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            },
            { withCredentials: true },
          );
          if (onVerified) {
            onVerified(verifyResp.data);
          }
          resolve(verifyResp.data);
        } catch (err) {
          reject(err);
        }
      },
      modal: {
        ondismiss: () => {
          if (onDismiss) onDismiss();
          reject(new Error('Payment cancelled'));
        },
      },
      notes: { receipt },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', () => {
      reject(new Error('Payment failed'));
    });
    rzp.open();
  });
}

export default openRazorpayCheckout;
